<template>
  <div>
    <div class="pagination-margin" data-qid="basic-info-section">
      <div class="" v-if="((activity.additional_client && getClient.slug !== activity.additional_client.slug) || activity.additional_client == null)">

        <!-- Engagements: all tasks grouped by control objective -->
        <template v-if="isEngagementProject">
          <EngagementTaskList
              ref="engagementTaskList"
              :project="activity"
              :tasks="Array.isArray(open_tasks) ? open_tasks : []"
              :loading="loadingTasks"
              :canEdit="isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
              :canManageAssignees="engagementPermissions(activity).manage && activity.status !== 'closed' && activity.status !== 'completed'"
              @manage-assignees="$refs.taskAssigneesModal.show()"
              @reminders="showReminderModal(object)"
              @add-task="showAddTaskModal"
              @assign="showEditUsersModal($event, 'default')"
              @delete="showConfirmDeleteModal"
              @edit-objective="editObjective"
              @delete-objective="deleteObjective"
              @reload="reloadTasks"
          ></EngagementTaskList>
          <EngagementObjectiveModal ref="objectiveModal" @saved="objectiveChanged"></EngagementObjectiveModal>
          <!-- Deleting a task or a control objective -->
          <EngagementConfirmModal ref="deleteTaskConfirm"
                                  :title="$t('tasks.v2.list.delete_task')"
                                  :eyebrow="itemToDelete.reference"
                                  :confirmLabel="$t('tasks.v2.list.delete_task')"
                                  :busy="buttonDisabled"
                                  qid="delete-task-v2"
                                  @confirm="confirmDeleteS(itemToDelete)"
          >
            <b>{{ itemToDelete.title || itemToDelete.full_title }}</b>
            <div>{{ $t('tasks.v2.list.delete_task_text') }}</div>
          </EngagementConfirmModal>
          <EngagementConfirmModal ref="deleteObjectiveConfirm"
                                  :title="$t('tasks.v2.list.delete_objective')"
                                  :eyebrow="$t('tasks.v2.objective.eyebrow')"
                                  :confirmLabel="$t('tasks.v2.list.delete_objective')"
                                  :busy="buttonDisabled"
                                  qid="delete-objective-v2"
                                  @confirm="confirmDeleteObjective"
          >
            <b>{{ managedObjective.name }}</b>
            <div>{{ $t('tasks.v2.list.delete_objective_text') }}</div>
          </EngagementConfirmModal>
        </template>

        <section class="panel" v-if="open_tasks && !isEngagementProject">
          <header class="panel__header">
            <div class="profile-compact">
              <div class="logo-container">
                <font-awesome-icon icon="tasks" class="icon-svg--sm"/>
              </div>
              <div class="profile-compact__info">
                <div class="profile-compact__top">
                  <div class="profile-compact__text">
                    {{ $t('projects.tab.tasks') }}
                    <span class="section-compact__secondary">
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="profile-compact__actions">
              <span class="icon-btn"
                    v-if="isEngagementProject && engagementPermissions(activity).manage && activity.status !== 'closed' && activity.status !== 'completed'"
                    @click="$refs.taskAssigneesModal.show()"
                    data-qid="manage-task-assignees-button"
                    v-b-tooltip.hover
                    :title="$t('projects.engagement.assignees.button')"
              >
                <font-awesome-icon icon="user-tag"/>
              </span>
              <span class="icon-btn"
                    v-if="!isEngagementProject && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                    @click="showDefaultAssigneesModal(object)"
                    data-qid="question-send_reminder-button"
                    v-b-tooltip.hover
                    :title="$t('projects.create.task_assignees')"
              >
                <font-awesome-icon icon="user-tag"/>
              </span>
              <span class="icon-btn"
                    v-if="isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                    @click="showReminderModal(object)"
                    data-qid="question-send_reminder-button"
                    v-b-tooltip.hover
                    :title="$t('tasks.send_reminders')"
              >
                <font-awesome-icon icon="reply-clock"/>
              </span>
              <span class="icon-btn"
                    @click="showAddTaskModal"
                    v-if="isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.type && activity.type !== 'risk_assessment' && activity.status !== 'closed' && activity.status !== 'completed'"
                    data-qid="question-create-button"
                    :disabled="loadingTasks"
                    v-b-tooltip.hover
                    :title="$t('tasks.assessment.add_from_template')"
              >
                <font-awesome-icon icon="plus"/>
              </span>
              <span class="icon-btn"
                    @click="downloadTasks"
                    data-qid="reporting-download_media-button"
                    v-if="isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.type && (activity.type === 'update' || activity.type === 'validation' || activity.type === 'security_measure' || activity.type === 'control')"
              >
                <font-awesome-icon icon="file-download"/>
              </span>
              <span class="icon-btn hide-on-mobile"
                    @click="handleFilterButton(false)"
                    data-qid="projects-filter-button"
                    v-b-tooltip.hover
                    :title="$t('tasks.table.task_filters')"
              >
                <font-awesome-icon icon="filter"/>
              </span>
            </div>
          </header>

          <div class="row m-0" :class="{'table-with-filters': filterOpened}">
            <div class="col-sm-2 tree-filter-col hide-on-mobile">
              <div class="filter-tree-view-container" style="border-radius: 0;">
                <div class="p-0 tree-filter-col">
                  <AssessmentTaskSideFilter
                      v-if="SideFilterRenderComponent"
                      :selectedCategory="selectedCategory"
                      :updateList="updateChildList"
                      :assessment="activity"
                      @updateSelected="updateSelectedFilter"
                      :loadingTasks="loadingTasks"
                      ref="sideFilter"
                  ></AssessmentTaskSideFilter>
                </div>
              </div>
            </div>
            <div class="col-sm-12 table-container-12 mobile-only">
              <div class="filter-tree-view-container">
                <div class="p-0 tree-filter-col">
                  <AssessmentTaskSideFilter
                      v-if="SideFilterRenderComponent"
                      :selectedCategory="selectedCategory"
                      :updateList="updateChildList"
                      :assessment="activity"
                      @updateSelected="updateSelectedFilter"
                      :loadingTasks="loadingTasks"
                      ref="sideFilter"
                  ></AssessmentTaskSideFilter>
                </div>
              </div>
            </div>

            <div class="col-sm-8 table-container-8 hide-on-mobile">
              <AssessmentTaskTopBar
                :selectedCategory="selectedCategory"
                :selectedProject="activity"
                @forceSideFilterRenderComponent="forceSideFilterRenderComponent"
                @forceSideFilterRender="forceSideFilterRender"
                @reloadSideFilter="reloadSideFilter"
              ></AssessmentTaskTopBar>

              <div class="tree-result-main">
                <table class="table table-responsive-md table-list-secondary">
                  <thead>
                  <tr>
                    <th v-if="selectedCategory !== 'all'"></th>
                    <th>
                      {{ $t('projects.table.name') }}
                    </th>
                    <th>
                      {{ $t('tasks.table.task_status') }}
                    </th>
                    <th>
                      {{ $t('tasks.modal.response_label') }}
                    </th>
                    <th>
                      {{ $t('system.quality_control') }}
                    </th>
                    <th style="width: 210px"></th>
                  </tr>
                  </thead>
                  <draggable
                          v-if="open_tasks && open_tasks.length"
                          v-model="open_tasks"
                          tag="tbody"
                          :disabled="isDragDisabled"
                          @start="onDragStart"
                          @end="onDragEnd"
                          @change="onOrderChange"
                          handle=".drag-handle"
                          ghost-class="ghost-row"
                          chosen-class="chosen-row"
                          drag-class="drag-row"
                  >
                    <tr v-for="(object, index) in open_tasks"
                        :id="'table-row-' + index"
                        :key="'table-row-' + index">

                      <!-- Drag handle column (optional - you can remove this if you want the entire row to be draggable) -->
                      <td class="drag-handle-cell" v-if="selectedCategory !== 'all'">
                        <span class="drag-handle" title="Drag to reorder">
                          <font-awesome-icon icon="grip-vertical" class="text-muted" />
                        </span>
                      </td>

                      <b-popover
                          :target="'table-row-' + index"
                          boundary="viewport"
                          v-if="object.code === 'system_impact_assessment' || object.code === 'risk_assessment' || object.code === 'control_assessment'"
                          triggers="hover"
                          placement="topleft"
                          :delay="{ show: 500, hide: 0 }"
                      >
                        <div v-if="object.code === 'system_impact_assessment'">
                          <b-form-group>
                            <label data-qid="create-p-users-label">
                              {{ $t('processes.confidentiality.title') }}
                            </label>
                            <p v-if="object.confidentiality">
                              {{ object.confidentiality }}
                            </p>
                            <p v-if="!object.confidentiality">
                              {{ $t('system.no_results') }}
                            </p>
                          </b-form-group>
                          <b-form-group>
                            <label data-qid="create-p-users-label">
                              {{ $t('processes.integrity.title') }}
                            </label>
                            <p v-if="object.integrity">
                              {{ object.integrity }}
                            </p>
                            <p v-if="!object.integrity">
                              {{ $t('system.no_results') }}
                            </p>
                          </b-form-group>
                          <b-form-group>
                            <label data-qid="create-p-users-label">
                              {{ $t('processes.availability.title') }}
                            </label>
                            <p v-if="object.availability">
                              {{ object.availability }}
                            </p>
                            <p v-if="!object.availability">
                              {{ $t('system.no_results') }}
                            </p>
                          </b-form-group>
                          <b-form-group>
                            <label data-qid="create-p-users-label">
                              {{ $t('tasks.modal.rto_label') }}
                            </label>
                            <p v-if="object.rto_number">
                              {{ object.rto_number }} {{ object.rto_type }}
                            </p>
                            <p v-if="!object.rto_number">
                              {{ $t('system.no_results') }}
                            </p>
                          </b-form-group>
                          <b-form-group>
                            <label data-qid="create-p-users-label">
                              {{ $t('tasks.modal.rpo_label') }}
                            </label>
                            <p v-if="object.rpo_number">
                              {{ object.rpo_number }} {{ object.rpo_type }}
                            </p>
                            <p v-if="!object.rpo_number">
                              {{ $t('system.no_results') }}
                            </p>
                          </b-form-group>
                          <b-form-group>
                            <label data-qid="create-p-users-label">
                              {{ $t('tasks.modal.remarks_label') }}
                            </label>
                            <p class="text-formatted" v-if="object.remarks"><FormattedNoteText :value="object.remarks"/></p>
                            <p v-if="!object.remarks">{{ $t('system.no_results') }}</p>
                          </b-form-group>
                        </div>
                        <div v-if="object.code === 'control_assessment' || object.code === 'risk_assessment'">
                          <b-form-group v-if="object.users && object.users.length">
                            <label data-qid="create-p-users-label">
                              {{ $t('tasks.table.task_assignee') }}
                            </label>
                            <div class="mb-1" v-for="(user, user_index) in object.users" :key="user_index">
                              <span data-qid="profile-photo">
                                <Avatar :object="user" size="xs"></Avatar>
                                {{ user.name }}
                              </span>
                            </div>
                          </b-form-group>
                          <b-form-group v-if="object.status">
                            <label data-qid="create-p-users-label">
                              {{ $t('tasks.table.task_status') }}
                            </label>
                            <p v-if="object.status">
                              <span v-if="object.status === 'completed'" >
                                <font-awesome-icon icon="check-circle" class="icon-success"/>
                              </span>
                              <span v-if="object.status === 'pending'" class="pr-2">
                                <font-awesome-icon
                                    icon="exclamation-triangle"
                                    class="risk-6"
                                />
                              </span>
                              {{ $t('system.' + $options.filters.slugify(object.status)) }}
                            </p>
                          </b-form-group>
                          <b-form-group v-if="object.response">
                            <label data-qid="create-p-users-label">
                              {{ $t('tasks.modal.response_label') }}
                            </label>
                            <div v-if="object.response">
                              <span v-if="object.response_type === 'Datepicker'">
                                <font-awesome-icon icon="calendar-alt" class="mr-2"/>
                                {{ object.response | formatDate }}
                              </span>
                              <span v-else>
                                <ResponseIcon :options="object.response_type_options" :title="object.response"></ResponseIcon>
                                {{ object.response }}
                              </span>
                            </div>
                          </b-form-group>
                          <b-form-group v-if="object.control_description">
                            <label data-qid="create-p-users-label">
                              {{ $t('tasks.modal.control_description_label') }}
                            </label>
                            <p class="text-formatted"><FormattedNoteText :value="object.control_description"/></p>
                          </b-form-group>
                          <b-form-group v-if="object.performed_text">
                            <label data-qid="create-p-users-label">
                              {{ $t('tasks.modal.performed_test_label') }}
                            </label>
                            <p class="text-formatted"><FormattedNoteText :value="object.performed_text"/></p>
                          </b-form-group>
                          <b-form-group v-if="object.remarks">
                            <label data-qid="create-p-users-label">
                              {{ $t('tasks.modal.remarks_label') }}
                            </label>
                            <p class="text-formatted"><FormattedNoteText :value="object.remarks"/></p>
                          </b-form-group>
                          <b-form-group v-if="object.data_users && object.data_users.length">
                            <label data-qid="create-p-users-label">
                              {{ $t('tasks.table.data_collection_assignee') }}
                            </label>
                            <div class="mb-1" v-for="(user, data_user_index) in object.data_users" :key="data_user_index">
                              <span data-qid="profile-photo">
                                <Avatar :object="user" size="xs"></Avatar>
                                {{ user.name }}
                              </span>
                            </div>
                          </b-form-group>
                          <b-form-group v-if="object.data_remarks">
                            <label data-qid="create-p-users-label">
                              {{ $t('tasks.modal.comment_label') }}
                            </label>
                            <p class="text-formatted"><FormattedNoteText :value="object.data_remarks"/></p>
                          </b-form-group>
                          <b-form-group>
                            <label data-qid="create-p-users-label">
                              {{ $t('maintenance.policies.create.attachments') }}
                            </label>
                            <p>
                              <span v-if="(object.media && object.media.length) || (object.links && object.links.length)">
                                {{ $t('system.yes') }}
                              </span>
                              <span v-else>
                                {{ $t('system.no') }}
                              </span>
                            </p>
                          </b-form-group>
                        </div>
                      </b-popover>
                      <td>
                        <a href="#" v-if="object.code === 'business_risk_assessment' || object.code === 'privacy_risk_assessment' || object.code === 'system_impact_assessment'" @click="navigateToContent(object, 'model')">
                          <span class="mr-2 mt-2" v-if="object.model">
                            <font-awesome-icon icon="desktop" size="lg" class="mr-2"/>
                          </span>
                          <span v-if="object.model">
                            {{ object.model.name}}
                          </span>
                          <span class="text-muted" v-if="!object.model">
                            {{ $t('tasks.modal.content_deleted') }}
                          </span>
                        </a>
                        <span v-else>
                          <span v-if="activity.type && (activity.type === 'update' || activity.type === 'validation')">
                            {{ $t('tasks.values.' + $options.filters.slugify(object.title)) }}
                          </span>
                          <!-- Engagements: the title opens the task panel, with signals for chat and attachments -->
                          <a v-else-if="isEngagementProject && object.model && object.code !== 'update_measures'"
                             href="#"
                             class="v2-task-title"
                             @click.prevent="openTask(object.id)"
                             data-qid="task-row-open"
                          >{{ object.full_title }}</a>
                          <span v-else>
                            {{ object.full_title }}
                          </span>
                          <span v-if="isEngagementProject && (object.comment_unread_count || (object.media && object.media.length) || (object.linked_media && object.linked_media.length))" class="v2-signals">
                            <span v-if="object.comment_unread_count" class="v2-signal v2-signal--new">
                              <font-awesome-icon icon="comment" class="mr-1"/>{{ $tc('tasks.v2.new_messages', object.comment_unread_count, { count: object.comment_unread_count }) }}
                            </span>
                            <span v-if="(object.media && object.media.length) || (object.linked_media && object.linked_media.length)" class="v2-signal">
                              <font-awesome-icon icon="paperclip" class="mr-1"/>{{ (object.media ? object.media.length : 0) + (object.linked_media ? object.linked_media.length : 0) }}
                            </span>
                          </span>
                          <span v-if="isEngagementProject && object.code !== 'update_measures' && !(object.users && object.users.length)"
                                class="engagement-unassigned-pill"
                                data-qid="task-unassigned-flag"
                          >
                            {{ $t('projects.engagement.filter.unassigned') }}
                          </span>
                        </span>
                      </td>

                      <td>
                        <div class="task-status-cell">
                        <span v-if="object.status === 'completed'" >
                            <font-awesome-icon icon="check-circle" class="icon-success"/>
                            {{ $t('system.task_completed') }}
                        </span>
                        <span v-if="object.status === 'pending'" class="pr-2">
                          <span v-if="object.data_collection_on && object.data_status === 'completed'" >
                              <font-awesome-icon
                                      icon="info-circle"
                                      class="icon-info"
                              />
                              {{ $t('system.documentation_completed') }}
                          </span>
                          <span v-if="!object.data_collection_on" >
                              <font-awesome-icon
                                      icon="exclamation-triangle"
                                      class="risk-6"
                              />
                              {{ $t('system.in_progress') }}
                          </span>
                          <span v-if="object.data_collection_on && object.data_status !== 'completed'" >
                            <span v-if="object.data_remarks || getMedia(object).length">
                              <font-awesome-icon
                                      icon="exclamation-triangle"
                                      class="risk-6"
                              />
                              {{ $t('system.documentation_in_progress') }}
                            </span>
                            <span v-else>
                              <font-awesome-icon
                                      icon="exclamation-triangle"
                                      class="risk-6"
                              />
                              {{ $t('system.documentation_not_started') }}
                            </span>
                          </span>
                        </span>

                          <span v-if="object.status === 'pending' && object.test_phase === 'operating_effectiveness'" class="test-phase-pill" data-qid="task-row-oe-testing">
                            {{ $t('tasks.test_phase.operating_effectiveness') }}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span v-if="object.add_response === 'No' || !object.add_response">
                          {{ $t('system.n_a') }}
                        </span>
                        <span v-else-if="object.response">
                          <span v-if="object.response_type === 'Datepicker'">
                            <font-awesome-icon icon="calendar-alt" class="mr-2"/>
                            {{ object.response | formatDate }}
                          </span>
                          <span v-else>
                            <ResponseIcon :options="object.response_type_options" :title="object.response"></ResponseIcon>
                            {{ object.response }}
                          </span>
                        </span>
                        <span v-else>
                          <span v-if="object.status === 'pending'">
                            <span class="pr-2">
                              <font-awesome-icon
                                  icon="exclamation-triangle"
                                  class="risk-6"
                              />
                            </span>
                            {{ $t('system.' + $options.filters.slugify(object.status)) }}
                          </span>
                        </span>
                      </td>
                      <td>
                        <span v-if="object.status === 'completed'">
                          <span v-if="object.sign_off_count > 0" v-b-tooltip.hover :title="signersTooltip(object)">
                              <font-awesome-icon v-if="signOffReached(object)" icon="check-circle" class="icon-success"/>
                              <font-awesome-icon v-else icon="exclamation-triangle" class="sign-off-pending"/>
                              {{ $t('system.signed_off') }} ({{ signOffProgress(object) }})
                          </span>
                          <span v-else-if="object.qa_status === 'rejected'" class="pr-2">
                                <font-awesome-icon icon="times-circle" class="icon-danger"/>
                                {{ $t('system.rejected') }}
                          </span>
                          <span v-else>
                              <font-awesome-icon
                                      icon="exclamation-triangle"
                                      class="risk-6"
                              />
                              {{ $t('system.in_progress') }}
                          </span>
                        </span>
                        <span v-if="object.status !== 'completed'" >
                            <span v-if="object.qa_status === 'accepted'" >
                                <font-awesome-icon icon="check-circle" class="icon-success"/>
                                {{ $t('system.accepted') }}
                            </span>
                            <span v-else-if="object.qa_status === 'rejected'" class="pr-2">
                                  <font-awesome-icon icon="times-circle" class="icon-danger"/>
                                  {{ $t('system.rejected') }}
                            </span>
                            <span v-else>
                                <font-awesome-icon
                                        icon="info-circle"
                                        class="icon-info"
                                />
                              {{ $t('system.waiting') }}
                            </span>

                        </span>


                      </td>
                      <td
                          class="text-right"
                          style="width:180px;"
                          data-qid="button-field"
                      >
                        <span class="btn btn-edit"
                              @click="showEditUsersModal(object, 'default')"
                              v-if="isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                        >
                          <font-awesome-icon icon="user-tag" />
                        </span>
                        <span class="btn btn-edit"
                              @click="showViewTaskModal(object)"
                              v-if="object.status === 'completed' && (activity.type === 'security_measure' || activity.type === 'control' || activity.type === 'risk_assessment' || activity.type === 'bia_assessment')"
                        >
                          <font-awesome-icon icon="eye" />
                        </span>
                        <AssessmentSingleTaskModal
                            :task="object"
                            v-if="object.model && activity.type === 'bia_assessment' && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                            @reloadContent="reloadTasks"
                            style="display: inline-block;"
                        ></AssessmentSingleTaskModal>
                        <AssessmentGlobalSingleTaskModal
                            :task="object"
                            :controlObjective="selectedCategory"
                            :ref="'task-modal-' + object.id"
                            :panel="isEngagementProject || activity.type === 'risk_assessment'"
                            v-if="object.model && object.code !== 'update_measures' && (activity.type === 'risk_assessment' || activity.type === 'control_assessment') && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                            @reloadContent="reloadTasks"
                            @navigate="navigateTask(object.id, $event)"
                            style="display: inline-block;"
                        ></AssessmentGlobalSingleTaskModal>
                        <AssessmentGlobalMeasureSingleTaskModal
                            :task="object"
                            v-if="object.model && object.code === 'update_measures' && activity.type === 'control_assessment' && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                            @reloadContent="reloadTasks"
                            style="display: inline-block;"
                        ></AssessmentGlobalMeasureSingleTaskModal>
                        <RiskSingleTaskModal
                            :task="object"
                            @reloadContent="reloadTasks"
                            v-if="object.model && object.scope && (activity.type === 'security_measure' || activity.type === 'control') && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                            style="display: inline-block;"
                        ></RiskSingleTaskModal>
                        <UpdateSingleTaskModal
                            :task="object"
                            @reloadContent="reloadTasks"
                            v-if="object.model && (activity.type === 'update') && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                            style="display: inline-block;"
                        ></UpdateSingleTaskModal>
                        <ValidationSingleTaskModal
                            :task="object"
                            @reloadContent="reloadTasks"
                            v-if="object.model && (activity.type === 'validation') && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                            style="display: inline-block;"
                        ></ValidationSingleTaskModal>
                        <span v-if="activity.type === 'security_measure' || activity.type === 'control'">
                          <b-button
                              variant="delete"
                              @click="showConfirmDeleteModal(object)"
                              v-if="object.scope && object.model && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                              style="display: inline-block;"
                          >
                            <font-awesome-icon icon="trash-alt"/>
                          </b-button>
                        </span>
                        <span v-else>
                          <b-button
                              variant="delete"
                              @click="showConfirmDeleteModal(object)"
                              v-if="object.model && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                          >
                            <font-awesome-icon icon="trash-alt"/>
                          </b-button>
                        </span>
                      </td>
                    </tr>
                  </draggable>

                  <tbody v-if="open_tasks && !open_tasks.length && !loadingTasks">
                    <tr data-qid="no-results-row" class="text-center">
                      <td colspan="10">
                        <h5 class="mt-3">
                          {{ $t('system.no_results') }}
                        </h5>
                      </td>
                    </tr>
                  </tbody>

                  <tbody v-if="loadingTasks && !open_tasks.length">
                    <tr data-qid="loading-row" class="text-center">
                      <td colspan="10">
                        <div class="mt-2 mb-4">
                          <div class="swing-container">
                            <div class="swing">
                              <div class="swing-l"></div>
                              <div></div>
                              <div></div>
                              <div></div>
                              <div class="swing-r"></div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div class="mt-3 pagination row">
                  <div class="col-md-6">
                    <div class="row">
                      <div class="float-left">
                        <span class="text-muted pagination-counter">
                          {{ $t('pagination.entries_per_page') }}:
                        </span>
                      </div>
                      <div class="float-left">
                        <multiselect
                            v-model="tasksPerPage"
                            :options="perPageOptions"
                            track-by="value"
                            label="value"
                            :select-label="$t('system.dropdown_select_label')"
                            :selected-label="$t('system.dropdown_selected_label')"
                            :deselect-label="$t('system.dropdown_deselect_label')"
                            data-qid="create-org-form-organization-level"
                            @select="tasksPerPageChanged"
                            :placeholder="tasksSelectedPerPage"
                            @remove="tasksPerPageRemoved"
                        ></multiselect>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <b-pagination
                        class="float-right"
                        v-model="tasksCurrentPage"
                        :total-rows="tasksRows"
                        :per-page="tasksSelectedPerPage"
                        aria-controls="my-table-1"
                        @change="tasksPageChanged"
                        limit="1"
                    >
                      <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
                      <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
                      <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
                      <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
                      <div slot="ellipsis-text">
                        <b-spinner small type="grow"></b-spinner>
                        <b-spinner small type="grow"></b-spinner>
                        <b-spinner small type="grow"></b-spinner>
                      </div>
                      <span class="text-muted" slot="page" slot-scope="{ page, active }">
                        <span v-if="active">{{getTasksPerPage}}</span>
                      </span>
                    </b-pagination>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-sm-12 table-container-12 mobile-only">
              <AssessmentTaskTopBar
                  :selectedCategory="selectedCategory"
                  :selectedProject="activity"
                  @forceSideFilterRenderComponent="forceSideFilterRenderComponent"
                  @forceSideFilterRender="forceSideFilterRender"
                  @reloadSideFilter="reloadSideFilter"
              ></AssessmentTaskTopBar>
              <!-- My projects -->
              <div class="">
                <div class="card-list">
                  <div
                      v-for="(object, index) in open_tasks"
                      :key="'task-card-' + index"
                      :id="'tableTasks-row-' + index"
                      type="button"
                      class="card-row cursor-pointer"
                  >
                    <div class="card-row__main">
                      <div class="profile-compact__info">
                        <div class="profile-compact__top">
                          <div class="profile-compact__text">
                            <span class="profile-compact__name">
                              {{ object.full_title }}
                            </span>

                            <span class="section-compact__secondary">
                            <!-- Main task -->
                              <span class="due-pill" v-if="object.status === 'pending' && object.data_collection_on && object.data_status === 'completed'">
                                <font-awesome-icon icon="info-circle" class="mr-1" />
                                <span>
                                  {{ $t('system.documentation_completed') }}
                                </span>
                              </span>

                              <!-- Documentation collection -->
                              <span class="due-pill risk-6" v-if="object.status === 'pending' && !object.data_collection_on">
                                <font-awesome-icon icon="exclamation-triangle" class="risk-6 mr-1" />
                                <span>
                                  {{ $t('system.in_progress') }}
                                </span>
                              </span>
                              <span class="due-pill risk-6" v-if="object.data_collection_on && object.data_status !== 'completed' && (object.data_remarks || getMedia(object).length)">
                                <font-awesome-icon icon="exclamation-triangle" class="risk-6 mr-1" />
                                <span>
                                  {{ $t('system.documentation_in_progress') }}
                                </span>
                              </span>
                              <span class="due-pill risk-6" v-if="object.data_collection_on && object.data_status !== 'completed' && !object.data_remarks && !getMedia(object).length">
                                <font-awesome-icon icon="exclamation-triangle" class="risk-6 mr-1" />
                                <span>
                                  {{ $t('system.documentation_not_started') }}
                                </span>
                              </span>

                              <!-- QA -->
                              <span class="due-pill risk-6" v-if="object.status === 'completed' && object.sign_off_count > 0" v-b-tooltip.hover :title="signersTooltip(object)">
                                <font-awesome-icon v-if="signOffReached(object)" icon="check-circle" class="icon-success mr-1" />
                                <font-awesome-icon v-else icon="exclamation-triangle" class="sign-off-pending mr-1" />
                                <span>
                                  {{ $t('system.signed_off') }} ({{ signOffProgress(object) }})
                                </span>
                              </span>
                              <span class="due-pill risk-6" v-if="object.status === 'completed' && object.qa_status === 'rejected'">
                                <font-awesome-icon icon="times-circle" class="icon-danger mr-1" />
                                <span>
                                  {{ $t('system.rejected') }}
                                </span>
                              </span>
                              <span class="due-pill risk-6" v-if="object.status === 'completed' && !object.sign_off_count && object.qa_status !== 'rejected'">
                                <font-awesome-icon icon="exclamation-triangle" class="risk-6 mr-1" />
                                <span>
                                  {{ $t('system.waiting') }}
                                </span>
                              </span>

                              <!-- Response pill-->
                              <span class="due-pill" v-if="object.add_response === 'Yes' || object.add_response">
                                <span v-if="object.response_type === 'Datepicker'">
                                  <font-awesome-icon icon="calendar-alt" class="mr-2"/>
                                  {{ object.response | formatDate }}
                                </span>
                                <span v-else>
                                  <ResponseIcon :options="object.response_type_options" :title="object.response"></ResponseIcon>
                                  {{ object.response }}
                                </span>
                              </span>
                              <span v-else>
                                <span v-if="object.status === 'pending'">
                                  <span class="pr-2">
                                    <font-awesome-icon
                                        icon="exclamation-triangle"
                                        class="risk-6"
                                    />
                                  </span>
                                  {{ $t('system.' + $options.filters.slugify(object.status)) }}
                                </span>
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="card-row__right">
                      <!-- Inline icon-only actions -->
                      <div class="">
                        <span class="btn btn-edit"
                              @click="showViewTaskModal(object)"
                              v-if="object.status === 'completed' && (activity.type === 'security_measure' || activity.type === 'control' || activity.type === 'risk_assessment' || activity.type === 'bia_assessment')"
                        >
                          <font-awesome-icon icon="eye" />
                        </span>
                        <AssessmentSingleTaskModal
                            :task="object"
                            v-if="object.model && activity.type === 'bia_assessment' && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                            @reloadContent="reloadTasks"
                            style="display: inline-block;"
                        ></AssessmentSingleTaskModal>
                        <AssessmentGlobalSingleTaskModal
                            :task="object"
                            :controlObjective="selectedCategory"
                            :panel="activity.type === 'risk_assessment'"
                            v-if="object.model && object.code !== 'update_measures' && (activity.type === 'risk_assessment' || activity.type === 'control_assessment') && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                            @reloadContent="reloadTasks"
                            style="display: inline-block;"
                        ></AssessmentGlobalSingleTaskModal>
                        <AssessmentGlobalMeasureSingleTaskModal
                            :task="object"
                            v-if="object.model && object.code === 'update_measures' && activity.type === 'control_assessment' && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                            @reloadContent="reloadTasks"
                            style="display: inline-block;"
                        ></AssessmentGlobalMeasureSingleTaskModal>
                        <RiskSingleTaskModal
                            :task="object"
                            @reloadContent="reloadTasks"
                            v-if="object.model && object.scope && (activity.type === 'security_measure' || activity.type === 'control') && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                            style="display: inline-block;"
                        ></RiskSingleTaskModal>
                        <UpdateSingleTaskModal
                            :task="object"
                            @reloadContent="reloadTasks"
                            v-if="object.model && (activity.type === 'update') && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                            style="display: inline-block;"
                        ></UpdateSingleTaskModal>
                        <ValidationSingleTaskModal
                            :task="object"
                            @reloadContent="reloadTasks"
                            v-if="object.model && (activity.type === 'validation') && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                            style="display: inline-block;"
                        ></ValidationSingleTaskModal>
                        <span v-if="activity.type === 'security_measure' || activity.type === 'control'">
                          <b-button
                              variant="delete"
                              @click="showConfirmDeleteModal(object)"
                              v-if="object.scope && object.model && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                              style="display: inline-block;"
                          >
                            <font-awesome-icon icon="trash-alt"/>
                          </b-button>
                        </span>
                        <span v-else>
                          <b-button
                              variant="delete"
                              @click="showConfirmDeleteModal(object)"
                              v-if="object.model && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status !== 'closed' && activity.status !== 'completed'"
                          >
                            <font-awesome-icon icon="trash-alt"/>
                          </b-button>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div
                      v-if="!loadingTasks && !open_tasks.length"
                      class="empty"
                  >
                    <h5 class="mb-0">{{ $t('system.no_results') }}</h5>
                  </div>

                  <div v-if="loadingTasks" class="empty">
                    <div class="mt-2 mb-2">
                      <div class="swing-container">
                        <div class="swing">
                          <div class="swing-l"></div>
                          <div></div><div></div><div></div>
                          <div class="swing-r"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="card-body">
                  <div class="pagination row">
                    <div class="col-md-4">
                    </div>
                    <div class="col-md-8">
                      <b-pagination
                          class="float-right"
                          v-model="tasksCurrentPage"
                          :total-rows="tasksRows"
                          :per-page="tasksSelectedPerPage"
                          aria-controls="my-table"
                          @change="tasksPageChanged"
                          limit="1"
                      >
                        <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
                        <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
                        <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
                        <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
                        <div slot="ellipsis-text">
                          <b-spinner small type="grow"></b-spinner>
                          <b-spinner small type="grow"></b-spinner>
                          <b-spinner small type="grow"></b-spinner>
                        </div>
                        <span class="text-muted" slot="page" slot-scope="{ page, active }">
                          <span v-if="active">{{getTasksPerPage}}</span>
                        </span>
                      </b-pagination>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-sm-2 filter-container">
              <div class="card">
                <div class="card-body">

                  <b-form-group>
                    <label data-qid="filter-p-type-label">
                      {{ $t('tasks.table.task_status') }}
                    </label>
                    <multiselect
                            v-model="filters.task_status"
                            :options="taskStatus"
                            :placeholder="$t('tasks.table.task_status')"
                            @select="debounceInput"
                            @remove="debounceInput"
                            select-label="✔"
                            selected-label="✔"
                            deselect-label="x"
                            track-by="name"
                            label="name"
                            qid="filter-p-form-process_group"
                    ></multiselect>
                  </b-form-group>
                  <hr/>
                  <b-form-group>
                    <label data-qid="filter-p-type-label">
                      {{ $t('system.quality_control') }}
                    </label>
                    <multiselect
                            v-model="filters.qa_status"
                            :options="qaStatus"
                            :placeholder="$t('system.quality_control')"
                            @select="debounceInput"
                            @remove="debounceInput"
                            select-label="✔"
                            selected-label="✔"
                            deselect-label="x"
                            track-by="name"
                            label="name"
                            qid="filter-p-form-process_group"
                    ></multiselect>
                  </b-form-group>
                  <hr/>
                  <b-form-group>
                    <label data-qid="filter-p-sign-off-label">
                      {{ $t('system.sign_off') }}
                    </label>
                    <multiselect
                            v-model="filters.sign_off"
                            :options="signOffOptions"
                            :placeholder="$t('system.sign_off')"
                            @select="debounceInput"
                            @remove="debounceInput"
                            select-label="✔"
                            selected-label="✔"
                            deselect-label="x"
                            track-by="name"
                            label="name"
                            qid="filter-p-form-sign_off"
                    ></multiselect>
                  </b-form-group>

                  <template v-if="isEngagementProject">
                    <hr/>
                    <b-form-group>
                      <label data-qid="filter-p-assignee-label">
                        {{ $t('projects.engagement.filter.assignee') }}
                      </label>
                      <multiselect
                              v-model="filters.assignee"
                              :options="assigneeFilterOptions"
                              :multiple="true"
                              :placeholder="$t('projects.engagement.filter.assignee')"
                              @select="debounceInput"
                              @remove="debounceInput"
                              select-label="✔"
                              selected-label="✔"
                              deselect-label="x"
                              track-by="value"
                              label="name"
                              qid="filter-p-form-assignee"
                      ></multiselect>
                    </b-form-group>
                  </template>

                  <a
                      href="#"
                      @click="resetFilters"
                      class="text-right"
                      data-qid="filter-s-reset"
                  >
                    {{ $t('system.reset_filters') }}
                  </a>
                </div>
              </div>
            </div>
          </div>

        </section>
      </div>
    </div>


    <!-- Re-assign modal -->
    <b-modal ref="re-assign" :modal-class="isEngagementProject ? 'task-modal v2-modal' : 'task-modal'" :size="isEngagementProject ? 'lg' : 'md'">
      <template #modal-header="{ close }">
        <!-- Engagements: the task in the title, a working close button -->
        <div v-if="isEngagementProject" class="v2-modal__head">
          <div class="v2-modal__titles">
            <div class="v2-modal__eyebrow">{{ itemObject.reference }} {{ itemObject.title }}</div>
            <h3 data-qid="re-assign-title">{{ $t('tasks.re_assign.title') }}</h3>
          </div>
          <span class="task-panel__nav"
                @click="hideEditUsersModal"
                data-qid="re-assign-close"
          ><font-awesome-icon icon="times"/></span>
        </div>
        <div v-else class="" style="width: 100%;">
          <span @click="hideEditUsersModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" data-qid="re-assign-title">
            {{ $t('tasks.re_assign.title') }}
          </h5>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="re-assign-error"
      >
        {{ error }}
      </b-alert>

      <b-form @submit="reassignFormSubmit" data-qid="re-assign-form" v-if="selectedTask && selectedTask.model">
        <div class="form-container">
          <b-form-group :class="{'v2-assign': isEngagementProject}">
            <label data-qid="re-assign-users-label">
              <span v-if="isEngagementProject" class="v2-assign__icon"><font-awesome-icon icon="user-tag"/></span>
              {{ $t('tasks.re_assign.users_label') }}
            </label>
            <font-awesome-icon v-if="!isEngagementProject" icon="exclamation-triangle" class="pl-1 risk-6" />
            <div v-if="isEngagementProject" class="v2-assign__hint">{{ $t('projects.engagement.assignees.main_hint') }}</div>

            <div class="multiselect-row">
              <multiselect
                  v-model="selectedAssignUsers"
                  :options="mainAssigneeOptions"
                  :multiple="true"
                  :taggable="false"
                  :disabled="buttonDisabled"
                  track-by="slug"
                  :select-label="$t('system.dropdown_select_label')"
                  :selected-label="$t('system.dropdown_selected_label')"
                  :deselect-label="$t('system.dropdown_deselect_label')"
                  :placeholder="$t('tasks.re_assign.users_placeholder')"
                  label="name"
                  qid="re-assign-users-select"
              >
                <template slot="tag" slot-scope="props">
                  <span class="mr-3 user-tag">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">{{ props.option.name }}</span>
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <UserDropdownOption :user="props.option"></UserDropdownOption>
                </template>
              </multiselect>

              <UserInviteModal
                    :buttonType="'small'"
                    :buttonDisabled=buttonDisabled
                    @reloadUserList="reloadUserList"
              ></UserInviteModal>
            </div>
          </b-form-group>
          <b-form-group v-if="itemObject.data_collection_on" :class="{'v2-assign': isEngagementProject}">
            <label data-qid="re-assign-users-label">
              <span v-if="isEngagementProject" class="v2-assign__icon v2-assign__icon--violet"><font-awesome-icon icon="folder-open"/></span>
              {{ $t('tasks.re_assign.data_collection_users_label') }}
            </label>
            <div v-if="isEngagementProject" class="v2-assign__hint">{{ $t('projects.engagement.assignees.data_collection_hint') }}</div>

            <div class="multiselect-row">
              <multiselect
                  v-model="selectedDataAssignUsers"
                  :options="allUsers"
                  :multiple="true"
                  :taggable="false"
                  :disabled="buttonDisabled"
                  track-by="slug"
                  :select-label="$t('system.dropdown_select_label')"
                  :selected-label="$t('system.dropdown_selected_label')"
                  :deselect-label="$t('system.dropdown_deselect_label')"
                  :placeholder="$t('tasks.re_assign.users_placeholder')"
                  label="name"
                  qid="re-assign-users-select"
              >
                <template slot="tag" slot-scope="props">
                  <span class="mr-3 user-tag">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">{{ props.option.name }}</span>
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <UserDropdownOption :user="props.option"></UserDropdownOption>
                </template>
              </multiselect>

              <UserInviteModal
                    :buttonType="'small'"
                    :buttonDisabled=buttonDisabled
                    @reloadUserList="reloadUserList"
              ></UserInviteModal>
            </div>
          </b-form-group>
        </div>
      </b-form>

      <template #modal-footer="{ close }">
        <div v-if="isEngagementProject" class="v2-modal__footer">
          <b-button variant="secondary" @click="hideEditUsersModal" qid="re-assign-cancel-button">{{ $t('projects.create.cancel') }}</b-button>
          <b-button variant="success"
                    @click="reassignFormSubmit"
                    :disabled="buttonDisabled"
                    qid="re-assign-submit-button"
          >
            <font-awesome-icon v-if="buttonDisabled"
                               icon="spinner"
                               size="sm"
                               class="fa-spin mr-1"
            />{{ $t('tasks.modal.save') }}
          </b-button>
        </div>
        <div v-else class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class="mt-3"
                            variant="secondary"
                            @click="hideEditUsersModal"
                            qid="re-assign-cancel-button"
                  >
                    {{ $t('projects.create.cancel') }}
                  </b-button>
                  <b-button type="submit"
                            class="mt-3 ml-3"
                            variant="success"
                            @click="reassignFormSubmit"
                            :disabled="buttonDisabled"
                            qid="re-assign-submit-button"
                  >
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                    </span>
                    {{ $t('projects.create.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-modal>

    <!-- Update default assignees -->
    <TaskAssigneesModal
        v-if="isEngagementProject"
        ref="taskAssigneesModal"
        :project="activity"
        @updated="loadTasks()"
    ></TaskAssigneesModal>

    <b-modal ref="update-default-assignees"
             modal-class="task-modal"
             @hide="hideDefaultAssigneesModal">

      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideDefaultAssigneesModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" data-qid="update-default-assignees-title">
            {{ $t('projects.create.task_assignees') }}
          </h5>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-p-error"
      >
        {{ error }}
      </b-alert>

      <b-form @submit="updateDefaultAssigneesFormSubmit" qid="create-p-form">
        <div class="form-container">
          <b-form-group>
            <label data-qid="create-p-users-label">
              {{ $t('projects.create.default_task_assignees') }}
            </label>
            <font-awesome-icon icon="exclamation-triangle" class="pl-1 risk-6" />

            <div class="multiselect-row">
              <multiselect
                  v-model="selectedDefaultTaskUsers"
                  :options="userList"
                  :multiple="true"
                  :taggable="false"
                  :disabled="buttonDisabled"
                  @search-change="loadUsers"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  track-by="slug"
                  :placeholder="$t('projects.create.default_task_assignees')"
                  label="name"
                  qid="create-p-users-select"
              >
                <template slot="tag" slot-scope="props">
                  <span class="mr-3 user-tag">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">{{ props.option.name }}</span>
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <UserDropdownOption :user="props.option"></UserDropdownOption>
                </template>
              </multiselect>

              <UserInviteModal
                    :buttonType="'small'"
                    :buttonDisabled=buttonDisabled
                    @reloadUserList="reloadUserList"
              ></UserInviteModal>
            </div>

            <b-form-checkbox
              v-model="applyOnTasks"
              v-if="activity.control_type !== 'continuous'"
              name="checkbox-disable"
              :value="1"
              :unchecked-value="0"
            >
              Apply for all tasks
            </b-form-checkbox>
          </b-form-group>

          <b-form-group class="pt-3 mb-0">
            <label data-qid="create-p-users-label">
              {{ $t('projects.create.data_collection_task_assignees') }}
            </label>

            <div class="multiselect-row">
              <multiselect
                  v-model="selectedDefaultDataCollectionUsers"
                  :options="userList"
                  :multiple="true"
                  :taggable="false"
                  @search-change="loadUsers"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  track-by="slug"
                  :placeholder="$t('projects.create.data_collection_task_assignees')"
                  label="name"
                  qid="create-p-users-select"
              >
                <template slot="tag" slot-scope="props">
                  <span class="mr-3 user-tag">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">{{ props.option.name }}</span>
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <UserDropdownOption :user="props.option"></UserDropdownOption>
                </template>
              </multiselect>

              <UserInviteModal
                    :buttonType="'small'"
                    :buttonDisabled=buttonDisabled
                    @reloadUserList="reloadUserList"
              ></UserInviteModal>
            </div>

            <b-form-checkbox
              v-model="applyOnDataCollectionTasks"
              v-if="activity.control_type !== 'continuous'"
              name="checkbox-disable"
              :value="1"
              :unchecked-value="0"
            >
              Apply for all tasks
            </b-form-checkbox>
          </b-form-group>
        </div>
      </b-form>

      <!-- FOOTER -->
      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">

          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">
              </div>
              <div class="col-sm-6 text-right">
                <b-button class="mt-2"
                          variant="secondary"
                          @click="hideDefaultAssigneesModal"
                          qid="update-default-assignees-cancel-button"
                >
                  {{ $t('projects.create.cancel') }}
                </b-button>
                <b-button @click="updateDefaultAssigneesFormSubmit"
                          class="mt-2 ml-3"
                          variant="success"
                          :disabled="buttonDisabled"
                          qid="update-default-assignees-submit-button"
                >
                  <span v-if="buttonDisabled" class="mr-1">
                    <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                  </span>
                  {{ $t('projects.create.submit') }}
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-modal>


    <!-- Delete task modal template -->
    <b-modal ref="delete-task" modal-class="task-modal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideConfirmDeleteModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" data-qid="delete-task-title">
            {{ $t('activities.system.delete.title') }}
          </h5>
        </div>
      </template>

      <div class="mb-2 mt-3" style="font-size:18px;" v-if="itemToDelete && itemToDelete.id">
        <div>
          {{ $t('activities.system.delete.description') }}
          <span class="text-bold">
            {{ itemToDelete.full_title }}
          </span>
        </div>
        <div class="mt-2">
          {{ $t('maintenance.measures.delete.are_you_sure') }}
        </div>
      </div>

      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <span class="btn btn-secondary cursor-pointer"
                        @click="hideConfirmDeleteModal"
                        data-qid="delete-task-cancel-button"
                  >
                    {{ $t('maintenance.measures.delete.cancel') }}
                  </span>
                  <b-button type="submit"
                            class="ml-3"
                            variant="danger"
                            @click="confirmDeleteS(itemToDelete)"
                            :disabled="buttonDisabled"
                            qid="delete-task-submit-button"
                  >
                    {{ $t('maintenance.measures.delete.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-modal>


    <!-- Add tasks: pick template tasks that aren't in the project yet, or create a new one (US 9398) -->
    <b-modal ref="add-task" modal-class="v2-modal" size="lg" hide-footer @hidden="resetAddTask">
      <template #modal-header>
        <div class="us-modal-head">
          <div>
            <h5 class="mb-0" data-qid="create-p-title">{{ $t('task_add.title') }}</h5>
            <p class="us-modal-head__sub">{{ $t('task_add.subtitle') }}</p>
          </div>
          <button type="button" class="us-icon-btn" :aria-label="$t('task_dialog.close')" @click="hideAddTaskModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>

      <div class="us-form ta">
        <b-alert v-if="error.length" show variant="danger" qid="create-p-error">{{ error }}</b-alert>

        <div class="at-filter ta-search">
          <font-awesome-icon icon="search"/>
          <input v-model="addTaskFilter"
                 :placeholder="$t('task_add.search')"
                 :aria-label="$t('task_add.search')"
                 autocomplete="off"
                 qid="create-p-search"
          />
        </div>

        <div class="ta-list" qid="create-p-list">
          <div v-if="questionsLoading" class="ta-empty"><font-awesome-icon icon="spinner" class="fa-spin"/></div>
          <div v-else-if="!questions.length" class="ta-empty">
            <b>{{ $t('task_add.all_added') }}</b>
            <span>{{ $t('task_add.all_added_text') }}</span>
          </div>
          <div v-else-if="!addTaskGroups.length" class="ta-empty">{{ $t('system.no_results') }}</div>
          <template v-else>
            <div v-for="group in addTaskGroups" :key="group.key" class="ta-group">
              <div v-if="group.name" class="ta-group__head">
                <b>{{ group.name }}</b>
                <span class="us-muted">{{ $tc('template_view.questions_count', group.questions.length, { count: group.questions.length }) }}</span>
                <span class="us-grow"></span>
                <button type="button" class="v2-link ta-group__all" qid="create-p-select-group" @click="toggleAddTaskGroup(group)">
                  {{ group.questions.every(question => selectedQuestionIds.indexOf(question.id) !== -1) ? $t('task_add.none') : $t('task_add.all') }}
                </button>
              </div>
              <label v-for="question in group.questions"
                     :key="question.id"
                     class="ta-row"
                     :class="{ 'is-on': selectedQuestionIds.indexOf(question.id) !== -1 }"
                     qid="create-p-question"
              >
                <input type="checkbox" :value="question.id" v-model="selectedQuestionIds">
                <span class="at-ref">{{ question.reference }}</span>
                <span class="ta-row__text">
                  <b>{{ question.name }}</b>
                  <RichTextView v-if="question.description" class="ta-row__desc" :value="question.description" :headings="false" compact :truncate="140"></RichTextView>
                </span>
              </label>
            </div>
          </template>
        </div>

        <div class="us-modal-foot">
          <button type="button" class="v2-link ta-create" qid="create-p-new-task" @click="showCreateQForm()">
            <font-awesome-icon icon="plus" class="mr-1"/>{{ $t('task_add.create') }}
          </button>
          <span class="us-grow"></span>
          <b-button variant="secondary" qid="create-p-cancel-button" @click="hideAddTaskModal">{{ $t('task_dialog.cancel') }}</b-button>
          <b-button variant="success" :disabled="!selectedQuestionIds.length || buttonDisabled" qid="create-p-submit-button" @click="addTaskFormSubmit">
            <font-awesome-icon v-if="buttonDisabled" icon="spinner" class="fa-spin mr-1"/>{{ $tc('task_add.add', selectedQuestionIds.length, { count: selectedQuestionIds.length }) }}
          </b-button>
        </div>
      </div>
    </b-modal>


    <!-- Create a task in the project (US 9398) -->
    <TaskDialog ref="taskDialog"
                :adapter="taskAdapter"
                :objectives="projectObjectives"
                :objective-required="!!(activity.assessment && activity.assessment.type === 'control')"
                :can-create-objective="isGranted(permissions.CONTROL_OBJECTIVE_CREATE)"
                :read-only="projectLocked ? $t('task_dialog.locks.project') : ''"
                @create-objective="showCreateControlObjectiveModal"
                @saved="taskCreated"
    ></TaskDialog>

    <!-- Add new control objective -->
    <b-modal ref="add-control-objective"
             @hide="hideCreateControlObjectiveModal"
             modal-class="bottom-left task-modal"
    >
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideCreateControlObjectiveModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center" data-qid="add-co-title">
            {{ $t('maintenance.control_objective.create.title') }}
          </h5>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="add-co-error"
      >
        {{ error }}
      </b-alert>

      <b-form @submit="coFormSubmit" data-qid="create-co-form">
        <div class="form-container">
          <b-form-group>
            <label data-qid="create-control-objective-name-label">
              {{ $t('maintenance.control_objective.create.name_label') }}<span class="mandatory"> *</span>
            </label>
            <b-form-input
                    data-qid="create-control-objective-form-name"
                    type="text"
                    v-model="coForm.name"
                    :maxLength="255"
                    required
                    :placeholder="$t('maintenance.control_objective.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label data-qid="create-control-objective-description-label">
              {{ $t('maintenance.control_objective.create.description_label') }}
            </label>
            <b-form-textarea
                    data-qid="create-control-objective-form-description"
                    v-model="coForm.description"
                    rows="6"
                    max-rows="6"
                    :placeholder="$t('maintenance.control_objective.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>
        </div>
      </b-form>

      <!-- FOOTER -->
      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">

          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">
              </div>
              <div class="col-sm-6 text-right">
                <b-button class=""
                          variant="secondary"
                          @click="hideCreateControlObjectiveModal"
                          data-qid="create-control-objective-cancel-button"
                >
                  {{ $t('maintenance.control_objective.create.cancel') }}
                </b-button>
                <b-button @click="coFormSubmit()"
                          class="ml-3"
                          variant="success"
                          :disabled="buttonDisabled"
                          data-qid="create-control-objective-submit-button"
                >
                        <span v-if="buttonDisabled" class="mr-1">
                          <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                        </span>
                  {{ $t('maintenance.control_objective.create.submit') }}
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-modal>

    <TaskViewModal
        :selectedTask="selectedTask"
        :showModal="viewTaskModal"
        @closeViewModal="hideViewTaskModal"
    ></TaskViewModal>

    <ShowMoreModal
        :items="selectedItem.users"
        :show="showUsersModal"
        @closeViewModal="closeUsersModal"
        type="users"
    ></ShowMoreModal>

    <ProjectViewModal
        :slug="selectedProjectSlug"
        :showModal="viewProjectModal"
        @closeViewModal="closeReminderModal"
    ></ProjectViewModal>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import Avatar from '@/components/Avatar.vue';
  import UserDropdownOption from '@/components/UserDropdownOption.vue';
  import draggable from 'vuedraggable'
  // import Datepicker from 'vuejs-datepicker';
  import ShowMoreModal from '@/components/ShowMoreModal.vue';
  import AssessmentSingleTaskModal from '@/views/Tasks/AssessmentSingleTaskModal.vue';
  import AssessmentGlobalSingleTaskModal from '@/views/Tasks/AssessmentGlobalSingleTaskModal.vue';
  import AssessmentGlobalMeasureSingleTaskModal from '@/views/Tasks/AssessmentGlobalMeasureSingleTaskModal.vue';
  import RiskSingleTaskModal from '@/views/Tasks/RiskSingleTaskModal.vue';
  import UpdateSingleTaskModal from '@/views/Tasks/UpdateSingleTaskModal.vue';
  import ValidationSingleTaskModal from '@/views/Tasks/ValidationSingleTaskModal.vue';
  import TaskViewModal from '@/views/Tasks/TaskViewModal.vue';
  import AssessmentTaskSideFilter from '@/components/tasks/AssessmentTaskSideFilter.vue';
  import AssessmentTaskTopBar from '@/components/tasks/AssessmentTaskTopBar.vue';
  import ProjectViewModal from '@/views/Projects/ProjectViewModal.vue';
  import UserInviteModal from '@/components/user/UserInviteModal.vue';
  import GuideButton from "@/components/guide/GuideButton.vue";
  import TaskAssigneesModal from '@/views/Projects/Engagement/TaskAssigneesModal.vue';
  import EngagementTaskList from '@/views/Projects/Engagement/EngagementTaskList.vue';
  import EngagementObjectiveModal from '@/views/Projects/Engagement/EngagementObjectiveModal.vue';
  import EngagementConfirmModal from '@/views/Projects/Engagement/EngagementConfirmModal.vue';
  import { isEngagement, engagementPermissions } from '@/views/Projects/Engagement/engagement.js';
  import FormattedNoteText from '@/components/tasks/FormattedNoteText.vue';
  import RichTextEditor from '@/components/richText/RichTextEditor.vue';
  import TaskDialog from '@/components/assessment/TaskDialog.vue';
  import RichTextView from '@/components/richText/RichTextView.vue';
  import { projectAdapter } from '@/components/assessment/taskDialogAdapters.js';
  import ResponseIcon from '@/components/assessment/ResponseIcon.vue';

  export default {
    name: 'TasksTable',
    // The engagement task panel asks the list for previous / next task and its position
    provide() {
      return { taskListNav: this };
    },
    data: function() {
      return {
        projectObjectives: [],
        showDocumentation: false,
        sForm: {
          items: []
        },
        form: {
          due_date: '',
          start_date: '',
          message: ''
        },
        eform: {
          due_date: '',
          start_date: '',
          message: ''
        },
        keywordValue: '',
        filters: {
          category: '',
          keywordValue: '',
          task_status: '',
          qa_status: '',
          sign_off: '',
          assignee: []
        },
        activity: {},
        sTableItems: [],
        selectedS: [],
        sLoading: true,
        error: '',
        itemToDelete: {},
        itemObject: {},
        buttonDisabled: false,
        open_tasks: {},
        loadingTasks: false,
        closed_tasks: {},
        showStatusForm: false,
        selectedStatus: [],

        //Default assignees
        userList: [],
        selectedDefaultTaskUsers: [],
        selectedDefaultDataCollectionUsers: [],
        applyOnTasks: 0,
        applyOnDataCollectionTasks: 0,
        defaultAssigneesForm: {
          scope_users: [],
          applyOnTasks: 0,
          data_collection_scope_users: [],
          applyOnDataCollectionTasks: 0,
        },

        // Project progress modal
        selectedProjectSlug: '',
        viewProjectModal: false,

        SideFilterRenderComponent: true,
        selectedCategory: {},
        // Control objective edited or deleted from the engagement task list
        managedObjective: {},

        options: [
          { name: this.$t('projects.status.pending'), value: 'pending' },
          { name: this.$t('projects.status.in_progress'), value: 'in progress' },
          { name: this.$t('projects.status.completed'), value: 'completed' },
          { name: this.$t('projects.status.closed'), value: 'closed' }
        ],
        selectedTask: [],
        selectedAssignUsers: [],
        selectedDataAssignUsers: [],
        selectedItem: {},
        showUsersModal: false,
        viewTaskModal: false,
        allUsers: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],

        questions: [],
        questionsLoading: false,
        selectedQuestionIds: [],
        addTaskFilter: '',

        // Tasks pagination
        tasksPerPage: '',
        tasksCurrentPage: 1,
        tasksSelectedPerPage: "10",
        tasksTotalRows: 0,
        updateChildList: false,
        editUserType: '',
        loadFirstTime: true,
        selectedControlObjective: [],
        assessmentControlOpen: false,
        controlObjectives: [],
        coForm: {
          name: '',
          description: ''
        },
        files: [],
        filterOpened: false,
        files_two: [],
        data_collection_files: [],
        data_collection_two_files: [],
        data_collection_on: 0,
        responseType: [],
        responseTypeDoc: [],
        addControlDescription: [],
        addNotes: [],
        addTestingNotes: [],
        addPerformedText: [],
        addDescription: [],
        addResponse: [],
        addResponseDoc: [],
        addAttachments: [],
        dataCollectionAddDescription: [],
        dataCollectionAddAttachments: [],
        showLinksSection: false,
        showLinksSectionTwo: false,
        showQuestionLinksSection: false,
        showQuestionTwoLinksSection: false,
        referenceInvalid: false,
        selectedFrameworkControls: [],
        selectedFramework: [],
        selectedFrameworkControl: [],
        frameworkControlItems: [],
        frameworkItems: [],
        assessment: {},

        dataCollectionOptions: [
          {text: this.$t('system.yes'), value: 1},
          {text: this.$t('system.no'), value: 0},
        ],
        showDropActive: false,
        responseTypes: [
          {
            group: this.$t('maintenance.questions.answer_type.input_label'),
            items: [
              { name: this.$t('maintenance.questions.type.datepicker'), value: 'Datepicker' },
            ]
          },
          {
            group: this.$t('maintenance.questions.answer_type.dropdown_label'),
            items: [
              { name: this.$t('maintenance.questions.type.dropdown'), value: 'Dropdown'},
              { name: this.$t('maintenance.questions.type.yes/no'), value: 'Yes/No'}
            ]
          }
        ],
        allowTypes: [
          { name: this.$t('maintenance.questions.type.no'), value: 'No' },
          { name: this.$t('maintenance.questions.type.optional'), value: 'Optional' },
          { name: this.$t('maintenance.questions.type.mandatory'), value: 'Mandatory' },
        ],
        allowTypesDoc: [
          { name: this.$t('maintenance.questions.type.no'), value: 'No' },
          { name: this.$t('maintenance.questions.type.optional'), value: 'Optional' },
          { name: this.$t('maintenance.questions.type.mandatory'), value: 'Mandatory' },
          { name: this.$t('maintenance.questions.type.auditor'), value: 'Auditor' },
        ],
        allowTypesShort: [
          { name: this.$t('maintenance.questions.type.optional'), value: 'Optional' },
          { name: this.$t('maintenance.questions.type.mandatory'), value: 'Mandatory' },
        ],
        answerTypes: [
          {
            group: this.$t('maintenance.questions.answer_type.positive_negative_label'),
            items: [
              { name: this.$t('maintenance.questions.answer_type.neutral'), value: 'Neutral' },
              { name: this.$t('maintenance.questions.answer_type.positive'), value: 'Positive' },
              { name: this.$t('maintenance.questions.answer_type.negative'), value: 'Negative' },
            ]
          },
          {
            group: this.$t('maintenance.questions.answer_type.impact_assessment_label'),
            items: [
              { name: this.$t('maintenance.questions.answer_type.very_low'), value: 'Very low' },
              { name: this.$t('maintenance.questions.answer_type.low'), value: 'Low' },
              { name: this.$t('maintenance.questions.answer_type.medium'), value: 'Medium' },
              { name: this.$t('maintenance.questions.answer_type.high'), value: 'High' },
              { name: this.$t('maintenance.questions.answer_type.very_high'), value: 'Very high' },
            ]
          },
        ],
        taskStatus: [
          { name: this.$t('system.documentation_not_started'), value: 'documentation_not_started' },
          { name: this.$t('system.documentation_in_progress'), value: 'documentation_in_progress' },
          { name: this.$t('system.in_progress'), value: 'in_progress' },
          { name: this.$t('system.documentation_completed'), value: 'documentation_completed' },
          { name: this.$t('system.task_completed'), value: 'task_completed' },
        ],
        qaStatus: [
          { name: this.$t('system.waiting'), value: 'waiting' },
          { name: this.$t('system.in_progress'), value: 'in_progress' },
          { name: this.$t('system.rejected'), value: 'rejected' },
          { name: this.$t('system.accepted'), value: 'accepted' },
        ],
        signOffOptions: [
          { name: this.$t('system.signed_off_by_me'), value: 'signed_off_by_me' },
          { name: this.$t('system.not_signed_off_by_me'), value: 'not_signed_off_by_me' },
        ],
        yesNoOptions: [
          'Yes',
          'No'
        ],
        selectedOptions: [],
        selectedOptionsDoc: [],
        inputOption: '',
        inputOptionDoc: '',
        showInput: true,
        showInputDoc: true,
        positiveAnswersDoc: [],
        negativeAnswersDoc: [],
        yesValue: [],
        noValue: [],
        yesValueDoc: [],
        noValueDoc: [],
        allAnswersDoc: [],
        allAnswersCopyDoc: [],
        isDragDisabled: false,
        dragStartIndex: null,
        dragEndIndex: null
      }
    },
    props: {
      object: {
        type: Object,
        required: true
      }
    },
    components: {
      ResponseIcon,
      FormattedNoteText,
      RichTextEditor,
      TaskDialog,
      RichTextView,
      UserDropdownOption,
      GuideButton,
      Avatar,
      // Datepicker,
      ShowMoreModal,
      RiskSingleTaskModal,
      AssessmentSingleTaskModal,
      AssessmentGlobalMeasureSingleTaskModal,
      TaskViewModal,
      UpdateSingleTaskModal,
      AssessmentTaskTopBar,
      AssessmentTaskSideFilter,
      ValidationSingleTaskModal,
      ProjectViewModal,
      UserInviteModal,
      AssessmentGlobalSingleTaskModal,
      TaskAssigneesModal,
      EngagementTaskList,
      EngagementObjectiveModal,
      EngagementConfirmModal,
      draggable,
    },
    computed: {
      // Completed and closed projects are read-only until they are reopened
      projectLocked() {
        return this.activity.status === 'closed' || this.activity.status === 'completed';
      },
      taskAdapter() {
        return projectAdapter(this.activity);
      },
      // Template tasks not in the project yet, filtered and grouped by control objective
      addTaskGroups() {
        let keyword = this.addTaskFilter.trim().toLowerCase();
        let groups = [];
        this.questions.forEach(question => {
          if (keyword && ![question.name, question.reference].some(value => value && value.toLowerCase().indexOf(keyword) !== -1)) {
            return;
          }
          let objective = question.control_objective || null;
          let key = objective ? objective.id : 'none';
          let group = _.find(groups, { key: key });
          if (!group) {
            group = { key: key, name: objective ? objective.name : '', order: objective ? objective.order : 0, questions: [] };
            groups.push(group);
          }
          group.questions.push(question);
        });
        return _.sortBy(groups, 'order');
      },
      // Disable drag if activity is closed/completed or user lacks permissions
      shouldDisableDrag() {
        return !this.selectedCategory || this.activity.status === 'closed' ||
          this.activity.status === 'completed'
      },
      getClient() {
        return this.$store.state.client
      },
      isEngagementProject() {
        return isEngagement(this.activity);
      },
      mainAssigneeOptions() {
        // Only engagement team members can be assigned to main tasks
        return this.isEngagementProject ? (this.activity.users || []) : this.allUsers;
      },
      assigneeFilterOptions() {
        // Engagement team members and tasks without a main assignee
        return [{ name: this.$t('projects.engagement.filter.unassigned'), value: 'unassigned' }].concat(
          _.map(_.sortBy(this.activity.users || [], 'name'), user => ({ name: user.name, value: user.id }))
        );
      },
      getFiles() {
        return this.selectedFiles
      },
      getUser() {
        return this.$store.state.user
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getTasksPerPage() {
        let page = this.tasksCurrentPage;
        let selectedFrom = (page-1) * Number(this.tasksSelectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.tasksSelectedPerPage) + Number(this.tasksSelectedPerPage);
        let selectedOf = this.tasksTotalRows;

        if (selectedTo > selectedOf) {
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      },
      tasksRows() {
        // Use computed value for rows because of dynamic update
        return this.tasksTotalRows
      }
    },
    watch: {
      shouldDisableDrag: {
        handler(newVal) {
          this.isDragDisabled = newVal
        },
        immediate: true
      },
      // Watch when client changes in store and trigger api reload
      getClient: function () {
      },
      object: function (value) {
        this.activity = value
        this.assessment = value.assessment
      }
    },
    methods: {
      engagementPermissions(project) {
        return engagementPermissions(project);
      },
      onDragStart(evt) {
        this.dragStartIndex = evt.oldIndex
        console.log('Drag started from index:', evt.oldIndex)

        // Add visual feedback
        document.body.classList.add('dragging')
      },

      onDragEnd(evt) {
        this.dragEndIndex = evt.newIndex
        console.log('Drag ended at index:', evt.newIndex)

        // Remove visual feedback
        document.body.classList.remove('dragging')
      },

      onOrderChange(evt) {
        console.log('Order changed:', evt)

        if (evt.moved) {
          const { oldIndex, newIndex } = evt.moved
          console.log(`Item moved from position ${oldIndex} to ${newIndex}`)

          // Call your API to save the new order
          this.saveTaskOrder()
        }
      },
      async saveTaskOrder() {
        try {
          // Create array of task IDs in new order
          const taskOrder = this.open_tasks.map((task, index) => ({
            id: task.id,
            order: index + 1
          }))

          // Call your API endpoint to save the order
          // Replace this with your actual API call
          piincHttp.post('projects/' + this.activity.slug + '/reorder', {
            project_id: this.activity.id,
            tasks: taskOrder
          }).then(function(response) {
            self.loadTasks()
            self.$toastr('success', self.$t('system.order_saved'))
          }, function(error) {
            if (error.status !== 200) {
              self.error = error.data.message;
            }
          })

        } catch (error) {
          console.error('Error saving task order:', error)
          this.$toastr.error(this.$t('system.error_saving_order'))

          // Optionally reload tasks to revert changes
          this.loadTasks()
        }
      },
      resetFilters() {
        this.filters.task_status = [];
        this.filters.qa_status = [];
        this.filters.sign_off = [];
        this.filters.assignee = [];
        this.debounceInput();
      },
      handleFilterButton(forceOpened) {
        if (!this.filterOpened) {

        }
        if (forceOpened) {
          this.filterOpened = true
        } else {
          this.filterOpened = !this.filterOpened
        }
      },
      reloadSideFilter() {
        if (this.$refs.sideFilter) {
          this.$refs.sideFilter.reloadItems()
        }
      },
      lockSwitch(lockStatus) {
        if (lockStatus) {
          this.data_collection_on = 0;
        }else{
          this.data_collection_on = 1;
        }
      },
      showCreateControlObjectiveModal() {
        this.coForm.name = '';
        this.coForm.description = '';
        this.error = ''
        this.$refs['add-control-objective'].show()
      },
      hideCreateControlObjectiveModal() {
        this.coForm.name = '';
        this.coForm.description = '';
        this.error = ''
        this.loadCO()
        this.$refs['add-control-objective'].hide()
      },
      coFormSubmit(evt) {
        this.buttonDisabled = true;
        // let formData = new FormData();

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        this.coForm.client = this.getClient.slug;
        this.coForm.project_id = this.activity.id;

        piincHttp.post('/project-control-objectives', this.coForm).then(function(response) {
          self.hideCreateControlObjectiveModal();
          self.loadQuestions();

          self.loadProjectObjectives();
          self.$refs.taskDialog.setObjective(response.data);

          self.reloadSideFilter()
          self.buttonDisabled = false;

          self.$toastr('success', self.$t('maintenance.control_objective.create.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      },
      loadCO(query) {
        let self = this;
        piincHttp.get('/assessment-control-objectives', { params:
            {
              client: this.getClient.slug,
              assessment: this.activity.assessment.id,
              per_page: 100,
              keyword: query ? query : undefined
            }
        }).then(function(response) {
          self.controlObjectives = response.data.items

          if (self.controlObjectives && !self.controlObjectives.length && !query) {
            self.controlObjectives.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      debounceInput: _.debounce(function() {
        this.loadTasks()
      }, 400),
      reloadTasks() {
        this.loadTasks(this.tasksCurrentPage)
      },
      tasksPageChanged(page) {
        this.loadTasks(page)
      },
      getIcon(object) {
        let foundObject = {}
        let responseOptions = JSON.parse(object.response_type_options)

        foundObject = _.find(responseOptions, function(item) {
          return item.title === object.response;
        });

        if (foundObject) {
          return foundObject.value.value
        } else {
          return ''
        }

      },
      updateSelectedFilter(item) {
        if (!item) {
          this.filters.category = {}
          this.selectedFilterName = ''
        }else{
          this.filters.category = item
          this.selectedFilterName = item.name
        }

        this.selectedCategory = this.filters.category;
        this.loadingTasks = true;
        this.open_tasks = []
        this.debounceInput()
      },
      navigateToContent(object, type) {
        if (type === 'model') {
          if (object.model_type === 'App\\Activity\\Models\\Activity') {
            this.$router.push({ name: 'activities.view', params: { slug: object.model.slug }})
          }
          if (object.model_type === 'App\\Process\\Models\\Process') {
            this.$router.push({ name: 'processes.view', params: { slug: object.model.slug }})
          }
          if (object.model_type === 'App\\System\\Models\\System') {
            this.$router.push({ name: 'systems.view', params: { slug: object.model.slug, }})
          }
          if (object.model_type === 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty') {
            this.$router.push({ name: 'contracts.view', params: { slug: object.model.slug, }})
          }
        } else {
          if (object.scope_type === 'App\\Activity\\Models\\Activity') {
            this.$router.push({ name: 'activities.view', params: { slug: object.scope.slug }})
          }
          if (object.scope_type === 'App\\Process\\Models\\Process') {
            this.$router.push({ name: 'processes.view', params: { slug: object.scope.slug }})
          }
          if (object.scope_type === 'App\\System\\Models\\System') {
            this.$router.push({ name: 'systems.view', params: { slug: object.scope.slug, }})
          }
          if (object.scope_type === 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty') {
            this.$router.push({ name: 'contracts.view', params: { slug: object.scope.slug, }})
          }
        }
      },
      tasksPerPageChanged(perPage) {
        let self = this;
        self.tasksSelectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.tasksSelectedPerPage);
        // Load content when page changed listener is triggered
        this.loadTasks(1, perPage.value)
      },
      tasksPerPageRemoved() {
        let self = this;
        // Load content when page changed listener is triggered
        self.tasksSelectedPerPage = self.perPageOptions[0].value
        this.loadTasks(1, self.tasksSelectedPerPage)
      },
      showViewTaskModal(object) {
        this.selectedTask = object
        this.viewTaskModal = true
      },
      hideViewTaskModal() {
        this.selectedTask = {}
        this.viewTaskModal = false
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.updateChildList = false;
        this.buttonDisabled = false;
        // Engagements use the V2 confirmation
        this.$refs[this.isEngagementProject ? 'deleteTaskConfirm' : 'delete-task'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        if (this.$refs.deleteTaskConfirm) {
          this.$refs.deleteTaskConfirm.hide()
        }
        this.$refs['delete-task'].hide()
      },
      openUsersModal(object) {
        this.selectedItem = object
        this.showUsersModal = true
      },
      closeUsersModal() {
        this.selectedItem = {}
        this.showUsersModal = false
      },
      resetFields() {
        this.selectedRC = null
      },
      cancelForm() {
        this.showStatusForm = false
      },
      hideAddTaskModal() {
        this.$refs['add-task'].hide();
      },
      resetAddTask() {
        this.selectedQuestionIds = [];
        this.addTaskFilter = '';
      },
      toggleAddTaskGroup(group) {
        let ids = group.questions.map(question => question.id);
        if (ids.every(id => this.selectedQuestionIds.indexOf(id) !== -1)) {
          this.selectedQuestionIds = this.selectedQuestionIds.filter(id => ids.indexOf(id) === -1);
        } else {
          this.selectedQuestionIds = _.union(this.selectedQuestionIds, ids);
        }
      },
      showAddTaskModal() {
        this.error = '';
        this.loadQuestions();
        this.updateChildList = false;
        this.$refs['add-task'].show();
      },
      // Open the task of a row (engagements open the task panel)
      openTask(taskId) {
        const host = this.$refs['task-modal-' + taskId];
        const instance = Array.isArray(host) ? host[0] : host;
        if (instance) {
          instance.showTaskModal(instance.task);
        }
      },
      // Previous / next task from the task panel, in the order of the list
      navigateTask(taskId, direction) {
        const tasks = _.filter(this.open_tasks || [], task => this.$refs['task-modal-' + task.id]);
        const index = _.findIndex(tasks, task => task.id === taskId);
        const next = tasks[index + direction];
        if (!next) {
          return;
        }
        const current = this.$refs['task-modal-' + taskId];
        const currentInstance = Array.isArray(current) ? current[0] : current;
        if (currentInstance) {
          currentInstance.hideTaskModal();
        }
        this.$nextTick(() => this.openTask(next.id));
      },
      // Position of a task in the list, for "Task 3 of 12" in the task panel
      taskPosition(taskId) {
        const tasks = _.filter(this.open_tasks || [], task => this.$refs['task-modal-' + task.id]);
        return { index: _.findIndex(tasks, task => task.id === taskId) + 1, total: tasks.length };
      },
      editObjective(objective) {
        this.$refs.objectiveModal.show(objective);
      },
      deleteObjective(objective) {
        this.managedObjective = objective;
        this.$refs.deleteObjectiveConfirm.show();
      },
      confirmDeleteObjective() {
        let self = this;
        this.buttonDisabled = true;
        piincHttp.delete('project-control-objectives/' + this.managedObjective.id).then(function() {
          self.buttonDisabled = false;
          self.$refs.deleteObjectiveConfirm.hide();
          self.$toastr('success', self.$t('projects.delete.success'));
          self.objectiveChanged();
        }, function(error) {
          self.buttonDisabled = false;
          self.$toastr('error', error.data && error.data.message ? error.data.message : '');
        });
      },
      objectiveChanged() {
        if (this.$refs.engagementTaskList) {
          this.$refs.engagementTaskList.loadObjectives();
        }
        this.reloadTasks();
      },
      showReminderModal(object) {
        this.selectedProjectSlug = object.slug;
        this.viewProjectModal = true;
      },
      closeReminderModal(statusChanged) {
        this.selectedProjectSlug = '';
        this.viewProjectModal = false;

        if (statusChanged) {
          this.loadProjects();
          this.loadTasks();
        }
      },
      showDefaultAssigneesModal(object) {
        this.loadUsers();
        this.selectedDefaultTaskUsers = object.scope_users;
        this.selectedDefaultDataCollectionUsers = object.data_collection_scope_users;

        this.defaultAssigneesForm.scope_users = [];
        this.defaultAssigneesForm.applyOnTasks = 0;
        this.defaultAssigneesForm.data_collection_scope_users = [];
        this.defaultAssigneesForm.applyOnDataCollectionTasks = 0;

        this.error = '';
        this.$refs['update-default-assignees'].show();
      },
      hideDefaultAssigneesModal() {
        this.error = '';
        this.$refs['update-default-assignees'].hide();
      },
      updateDefaultAssigneesFormSubmit(evt) {
        evt.preventDefault();
        this.updateDefaultTaskAssigneesFormSubmit();
        this.updateDefaultDataCollectionAssigneesFormSubmit();
        this.hideDefaultAssigneesModal();

      },
      updateDefaultTaskAssigneesFormSubmit() {
        let self = this;

        this.defaultAssigneesForm.scope_users = [];
        if (this.selectedDefaultTaskUsers && this.selectedDefaultTaskUsers.length) {
          for (var i = 0; i < this.selectedDefaultTaskUsers.length; i++) {
            this.defaultAssigneesForm.scope_users.push(this.selectedDefaultTaskUsers[i].id);
          }
        }

        this.defaultAssigneesForm.apply_on_tasks = this.applyOnTasks

        piincHttp.put('projects/' + this.activity.slug, {
          scope_users: this.defaultAssigneesForm.scope_users,
          apply_on_tasks: this.applyOnTasks
        }).then(function(response) {
          self.project = response.data
          self.applyOnTasks = 0;
          self.loadTasks()
          self.$toastr('success', self.$t('projects.update.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.error = error.data.message;
          }
        })
      },
      updateDefaultDataCollectionAssigneesFormSubmit() {
        let self = this;

        this.defaultAssigneesForm.data_collection_scope_users = [];
        if (this.selectedDefaultDataCollectionUsers && this.selectedDefaultDataCollectionUsers.length) {
          for (var i = 0; i < this.selectedDefaultDataCollectionUsers.length; i++) {
            this.defaultAssigneesForm.data_collection_scope_users.push(this.selectedDefaultDataCollectionUsers[i].id);
          }
        }

        this.defaultAssigneesForm.apply_on_data_collection_tasks = this.applyOnDataCollectionTasks

        piincHttp.put('projects/' + this.activity.slug, {
          data_collection_scope_users: this.defaultAssigneesForm.data_collection_scope_users,
          apply_on_tasks: this.applyOnDataCollectionTasks
        }).then(function(response) {
          self.project = response.data
          self.applyOnDataCollectionTasks = 0;
          // self.$emit('updated');
          self.loadTasks()
          self.$toastr('success', self.$t('projects.update.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.error = error.data.message;
          }
        })
      },
      // Adds the chosen template tasks one after the other, in the template's order
      addTaskFormSubmit() {
        let self = this;
        let ids = this.questions.map(question => question.id).filter(id => this.selectedQuestionIds.indexOf(id) !== -1);
        this.buttonDisabled = true;
        this.error = '';

        ids.reduce((previous, id) => previous.then(() => piincHttp.post('projects/' + this.object.slug + '/tasks', { question_id: id })), Promise.resolve())
          .then(function() {
            self.buttonDisabled = false;
            self.hideAddTaskModal();
            self.updateChildList = true;
            self.loadTasks();
            self.$toastr('success', self.$tc('task_add.added', ids.length, { count: ids.length }));
          }, function(error) {
            self.buttonDisabled = false;
            self.error = error && error.data && error.data.message ? error.data.message : self.$t('users_page.error');
            self.loadQuestions();
            self.loadTasks();
          });
      },
      forceSideFilterRenderComponent(object) {
          // store selected category provided from the top bar component
          this.selectedCategory = object
      },
      forceSideFilterRender() {
        this.SideFilterRenderComponent = false;
        this.SideFilterRenderComponent = true;
      },
      hideEditUsersModal() {
        this.selectedTask = {}
        this.itemObject = {}
        this.editUserType = ''
        this.$refs['re-assign'].hide()
      },
      // Create modals
      // "+" in Add task opens the task dialog for a new task in the project (US 9398)
      showCreateQForm() {
        this.$refs['add-task'].hide();
        this.loadProjectObjectives();
        this.$refs.taskDialog.open(null, null);
      },
      loadProjectObjectives() {
        let self = this;
        // Without "client": the project filter is enough, and the client filter only fits projects
        piincHttp.get('project-control-objectives', { params: {
          project: this.activity.id,
          per_page: 100
        }}).then(function(response) {
          self.projectObjectives = response.data.items;
        }, function() {});
      },
      taskCreated() {
        this.updateChildList = true;
        this.loadTasks();
        this.reloadSideFilter();
      },
      reloadUserList() {
        this.loadUsers();
      },
      loadUsers(query) {
        let self = this;

        this.loading = true;
        this.buttonDisabled = true;

        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            keyword: query ? query : undefined,
            per_page: 50
          }
        }).then(function(response) {
          self.userList = response.data.items
          self.buttonDisabled = false;
          self.loading = false;
        }, function() {});
      },
      showEditUsersModal(object) {
        let self = this;
        let role_name = [self.permissions.ROLE_CLIENT_ADMIN];

        role_name.push(self.permissions.ROLE_MEMBER);
        role_name.push(self.permissions.ROLE_VALIDATOR);

        if (this.getClient && this.getClient.parent) {
          role_name.push(self.permissions.ROLE_PARTITION_ADMIN);
        }

        this.error = '';
        this.itemObject = object;
        this.selectedAssignUsers = [];
        this.selectedDataAssignUsers = [];
        this.buttonDisabled = true;
        this.loading = true;

        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            role_name: role_name,
            per_page: 100
          }
        }).then(function(response) {
          let users = response.data.items

          _.forEach(object.users, function (user) {
            if (_.findIndex(users, function(o) { return o.id === user.id; }) === -1) {
              if (user.id !== self.getUser.id) {
                users.push(user)
              }
            }

            let matchedUser = _.cloneDeep(user)
            if (user.id === object.owner) {
              matchedUser.owner = true
            }
            self.selectedAssignUsers.push(matchedUser)
          })
          _.forEach(object.data_users, function (user) {
            if (_.findIndex(users, function(o) { return o.id === user.id; }) === -1) {
              if (user.id !== self.getUser.id) {
                users.push(user)
              }
            }

            let matchedUser = _.cloneDeep(user)
            if (user.id === object.owner) {
              matchedUser.owner = true
            }
            self.selectedDataAssignUsers.push(matchedUser)
          })

          self.allUsers = _.uniq(users)
          self.buttonDisabled = false;
          self.loading = false;
        }, function() {});

        this.selectedTask = object
        this.$refs['re-assign'].show()
      },
      reassignFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        let userIds = [];
        let datauserIds = [];
        evt.preventDefault();

        if (this.selectedAssignUsers && this.selectedAssignUsers.length) {
          for (let i = 0; i < this.selectedAssignUsers.length; i++) {
            userIds.push(this.selectedAssignUsers[i].id);
          }
        }
        if (this.selectedDataAssignUsers && this.selectedDataAssignUsers.length) {
          for (let i = 0; i < this.selectedDataAssignUsers.length; i++) {
            datauserIds.push(this.selectedDataAssignUsers[i].id);
          }
        }

        piincHttp.put('tasks/' + this.selectedTask.id, {
          users: userIds,
          data_users: datauserIds,
        }).then(function(response) {
          self.hideEditUsersModal();
          self.loadTasks()
          self.$toastr('success', self.$t('tasks.re_assign.success'))
          self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      },
      showFormFunction() {
        let self = this
        this.selectedStatus = []
        if (this.selectedS.status) {
          this.selectedStatus = _.find(this.options, function(item) {
            return item.value === self.selectedS.status;
          });
        }
        this.showStatusForm = true
      },
      sendReminders(object) {
        let self = this;
        // Call API for reminders
        piincHttp.get('projects/' + object.slug + '/reminders').then(function() {
          self.$toastr('success', self.$t('projects.create.project_reminders_success'))
        }, function() {});
      },
      loadTasks(page, perPage) {
        let self = this;
        this.loadingTasks = true;

        if (this.getStorePerPage) {
          this.tasksSelectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.tasksSelectedPerPage;

        if(self.activity.type === 'control_assessment'){
          // Call API for open tasks
          piincHttp.get('projects/' + this.object.slug + '/tasks', { params:
              {
                client: this.getClient.slug,
                page: pageNumber,
                not_code: 'update_measures',
                control_objective: this.selectedCategory && this.selectedCategory.id ? this.selectedCategory.id : undefined,
                task_status: this.filters.task_status ? this.filters.task_status.value : undefined,
                qa_status: this.filters.qa_status ? this.filters.qa_status.value : undefined,
                sign_off: this.filters.sign_off ? this.filters.sign_off.value : undefined,
                assignee: this.filters.assignee && this.filters.assignee.length ? _.map(this.filters.assignee, 'value') : undefined,
                // Engagements show all tasks, grouped by control objective
                per_page: this.isEngagementProject ? 1000 : perPageNumber,
              }
          }).then(function(response) {
            self.open_tasks = response.data.items;
            // Set number of total items
            self.tasksTotalRows = response.data.total_count;
            self.loadingTasks = false;
            self.loadFirstTime = false;
          }, function() {});
        }else{

          // Call API for open tasks
          piincHttp.get('projects/' + this.object.slug + '/tasks', { params:
              {
                client: this.getClient.slug,
                page: pageNumber,
                not_code: 'update_measures',
                model_type: this.selectedCategory && this.selectedCategory.who_am_i ? this.selectedCategory.who_am_i : undefined,
                model_id: this.selectedCategory && this.selectedCategory.id ? this.selectedCategory.id : undefined,
                per_page: perPageNumber,
              }
          }).then(function(response) {
            self.open_tasks = response.data.items;
            // Set number of total items
            self.tasksTotalRows = response.data.total_count;
            self.loadingTasks = false;
            self.loadFirstTime = false;
          }, function() {});
        }

      },
      loadClosedTasks() {
        let self = this;
        // Call API for closed tasks
        piincHttp.get('projects/' + this.object.slug + '/tasks', { params:
          {
            status: 'Completed',
            client: this.getClient.slug,
            per_page: 100
          }
        }).then(function(response) {
          self.closed_tasks = response.data.items;
        }, function() {});
      },
      downloadTasks() {
        // Call API for open tasks
        piincHttp.get('projects/' + this.object.slug + '/tasks/download',{responseType: 'blob'}).then(function(response) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'Project tasks.xlsx');

          link.click();
          link.remove()
          window.URL.revokeObjectURL(url);
        }, function() {});


      },
      getReducedUsers(users) {
        let reducedUsers = []
        if (users && users.length > 3) {
          reducedUsers = _.cloneDeep(users);
          reducedUsers = reducedUsers.slice(0, 3);
        } else {
          reducedUsers = users;
        }
        return reducedUsers;
      },
      showEditModal(object) {
        this.error = '';

        this.selectedS = object
        this.loadTasks()
        this.loadClosedTasks()
        this.eform.due_date = this.object.end_date;
        this.$refs['edit-item'].show()
      },
      hideEditModal() {
        this.selectedS = {}
        this.selectedUsers = []
        this.$refs['edit-item'].hide()
      },
      editFormSubmit(evt) {
        var moment = require('moment');
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        this.eform.due_date = this.eform.due_date ? moment(this.eform.due_date).format('YYYY-MM-DD') : '';

        piincHttp.put('projects/' + this.selectedS.slug, {end_date: this.eform.due_date}).then(function(response) {
          self.hideEditModal();
          self.loadProjects();
          self.$toastr('success', self.$t('projects.update.success'))
          self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      },
      showEditSModal(object) {
        this.eform.due_date = object.end_date;
        this.$refs['edit-system'].show()
      },
      hideEditSModal() {
        this.$refs['edit-system'].hide()
      },
      confirmDeleteS(object) {
        let self = this;
        piincHttp.delete('tasks/' + object.id).then(function() {
          self.hideConfirmDeleteModal();
          self.loadTasks();
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('tasks.delete.success'))
          self.$emit('updated');
        }, function() {});
      },
      showCreateModal() {
        this.error = '';
        this.$refs['create-item'].show()
      },
      hideCreateModal() {
        this.selectedS = {}
        this.selectedUsers = []
        this.form.due_date = ''
        this.form.start_date = ''
        this.$refs['create-item'].hide()
      },
      loadQuestions() {
        let self = this;
        this.questionsLoading = true;
        piincHttp.get('questions/no-tasks', { params:
          {
            client: this.getClient.slug,
            assessment: this.activity.assessment ? this.activity.assessment.id : undefined,
            project: this.activity.id,
            per_page: 1000
          }
        }).then(function(response) {
          self.questions = response.data.items;
          self.questionsLoading = false;
        }, function() {
          self.questionsLoading = false;
        });
      },
      requiredSignOffs() {
        return this.activity && this.activity.required_sign_offs ? parseInt(this.activity.required_sign_offs, 10) : 0;
      },
      // Green only once the project's required number of sign-offs is reached (at least one)
      signOffReached(task) {
        return task.sign_off_count > 0 && task.sign_off_count >= this.requiredSignOffs();
      },
      signOffProgress(task) {
        let required = this.requiredSignOffs();
        return required > 0 ? task.sign_off_count + '/' + required : task.sign_off_count;
      },
      signersTooltip(task) {
        if (!task.signers || !task.signers.length) {
          return '';
        }
        let signers = task.signers.map((signer) => {
          return signer.name + ' (' + this.$options.filters.formatDateTime(signer.signed_at) + ')';
        });
        return this.$t('system.signed_off_by') + ': ' + signers.join(', ');
      },
      getMedia(task) {
        let media = [];
          media = _.filter(task.media, function (mediaItem) {
            return mediaItem.collection_name === 'data_documents';
          });
        return media;
      },
    },
    created () {
      this.activity = this.object;
      // Engagements have no control objective side filter to trigger the first load
      if (this.isEngagementProject) {
        this.loadTasks();
      }
      if (this.getClient && this.getClient.slug) {

      }
    }
  }
</script>
<style scoped>
  /* Drag and drop styles */
  .draggable-row {
    transition: all 0.2s ease;
    cursor: move;
  }

  .draggable-row:hover {
    background-color: #f8f9fa;
  }

  .drag-handle-cell {
    width: 30px;
    padding: 8px 4px;
    text-align: center;
    cursor: grab;
  }

  .drag-handle {
    opacity: 0.5;
    transition: opacity 0.2s ease;
  }

  .drag-handle:hover {
    opacity: 1;
  }

  /* Dragging states */
  .ghost-row {
    opacity: 0.5;
    background-color: #e9ecef;
    transform: rotate(2deg);
  }

  .chosen-row {
    background-color: #fff3cd;
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  .drag-row {
    transform: rotate(2deg);
    opacity: 0.8;
    box-shadow: 0 8px 16px rgba(0,0,0,0.2);
  }

  /* Global dragging state */
  :global(.dragging) {
    user-select: none;
  }

  :global(.dragging .table) {
    overflow: visible;
  }

  /* Disable text selection during drag */
  .draggable-row.sortable-chosen {
    user-select: none;
  }

  /* Handle responsive behavior */
  @media (max-width: 768px) {
    .drag-handle-cell {
      display: none;
    }

    .draggable-row {
      cursor: move;
    }
  }

  /* Alternative: If you don't want a separate drag handle column */
  .full-row-drag .draggable-row {
    cursor: move;
  }

  .full-row-drag .drag-handle-cell {
    display: none;
  }
</style>
